import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, FlexProps } from 'rebass';
import Button from '../Button/Button';
import { Column, IdArrayInput, ItemLoader } from '../../hooks/useTablePages';
import { RightPaneConfig } from '../../hooks/useRightPaneConfiguration';
import { IconType } from '../Icon/Icon';

export type ActionBarProps<
  Item extends { [Id in IdField]: IdValue } = any,
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = FlexProps & {
  selectedIds?: IdArrayInput<IdValue>;
  numItems?: number;
  itemLoader?: ItemLoader<Item, IdField, IdValue>;
  columns?: Column[];
  csvFilename?: string;
  rightPaneConfig?: RightPaneConfig;
  setRightPaneConfig?: (value: RightPaneConfig | null) => void;
  rightPaneIcon?: IconType;
};

/**
 * @component
 */
const TableActionBar: React.FC<ActionBarProps> = (props) => {
  const {
    selectedIds = [],
    numItems = 0,
    itemLoader,
    columns = [],
    csvFilename,
    rightPaneConfig,
    setRightPaneConfig,
    rightPaneIcon,
    children,
  } = props;
  const [downloading, setDownloading] = useState(false);
  const downloadSelectedItems = useCallback(async () => {
    setDownloading(true);
    try {
      await itemLoader?.downloadIds(
        selectedIds,
        columns,
        csvFilename ?? 'data.csv'
      );
    } finally {
      setDownloading(false);
    }
  }, [itemLoader, selectedIds, columns]);
  const downloadAllItems = useCallback(async () => {
    setDownloading(true);
    try {
      await itemLoader?.downloadAll(columns, csvFilename ?? 'data.csv');
    } finally {
      setDownloading(false);
    }
  }, [itemLoader, columns]);

  return (
    <Flex justifyContent="space-between" {...(props as any)}>
      <Flex flexShrink={1} flexWrap="wrap" sx={{ gap: [1, 1, 1, 2] }}>
        {children}
        {numItems > 0 && (
          <>
            {selectedIds?.length > 0 && selectedIds?.length !== numItems && (
              <Button
                onClick={downloadSelectedItems}
                disabled={downloading || !selectedIds?.length}
                variant="outline"
                icon="download"
              >
                {downloading
                  ? 'Downloading...'
                  : `Download ${selectedIds.length}`}
              </Button>
            )}
            {(selectedIds?.length === 0 || selectedIds?.length === numItems) &&
              numItems > 0 && (
                <Button
                  onClick={downloadAllItems}
                  disabled={downloading || !numItems}
                  variant="outline"
                  icon="download"
                >
                  {downloading ? 'Downloading...' : `Download ${numItems}`}
                </Button>
              )}
          </>
        )}
      </Flex>
      {numItems > 0 && rightPaneConfig && (
        <Flex flexShrink={0}>
          <Button
            variant={rightPaneConfig.visible ? 'secondary' : 'outline'}
            icon={rightPaneIcon}
            onClick={() => {
              if (rightPaneConfig) {
                setRightPaneConfig?.({
                  ...rightPaneConfig,
                  visible: !rightPaneConfig?.visible,
                });
              }
            }}
            title={rightPaneConfig.visible ? 'Hide Map' : 'Show Map'}
          />
        </Flex>
      )}
    </Flex>
  );
};

TableActionBar.propTypes = {
  selectedIds: PropTypes.array,
  numItems: PropTypes.number,
  columns: PropTypes.array,
  itemLoader: PropTypes.any,
  children: PropTypes.any,
};

export default TableActionBar;
